import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    DialogActions,
    Button,
    Typography,
    Box,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuction } from '../context/AuctionContext';
import { useNotification } from '../context/NotificationContext';

const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

const BidsOverviewModal = ({ open, onClose }) => {
    const { auctionId } = useAuction();
    const notify = useNotification();
    const [closedRounds, setClosedRounds] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open && auctionId) {
            const fetchClosedRounds = async () => {
                setLoading(true);
                try {
                    // 1. Fetch all winning transfers for the auction
                    const transfersQuery = query(
                        collection(db, 'auctions', auctionId, 'auctionTransfers'),
                        where('type', '==', 'blindPoolAssignment')
                    );
                    const transfersSnap = await getDocs(transfersQuery);
                    const winningTransfers = transfersSnap.docs.map(doc => doc.data());

                    // 2. Fetch closed rounds
                    const roundsQuery = query(
                        collection(db, 'auctions', auctionId, 'blindPoolRounds'),
                        where('status', '==', 'closed'),
                        orderBy('closedAt', 'desc')
                    );
                    const roundsSnap = await getDocs(roundsQuery);
                    
                    const roundsData = await Promise.all(roundsSnap.docs.map(async (roundDoc) => {
                        const round = { id: roundDoc.id, ...roundDoc.data() };

                        // 3. Create a set of winning bids for this specific round for quick lookup
                        const winningBidsInRound = new Set();
                        winningTransfers
                            .filter(t => t.roundId === round.id)
                            .forEach(t => {
                                winningBidsInRound.add(`${t.playerId}-${t.to}`);
                            });
                        
                        // 4. Fetch bids for the round
                        const bidsQuery = query(
                            collection(db, 'auctions', auctionId, 'blindPoolRounds', round.id, 'bids')
                        );
                        const bidsSnap = await getDocs(bidsQuery);
                        let bids = bidsSnap.docs.map(d => ({...d.data(), id: d.id}));

                        // 5. Add winning status to each bid
                        bids = bids.map(bid => ({
                            ...bid,
                            isWinner: winningBidsInRound.has(`${bid.playerId}-${bid.participantId}`)
                        }));

                        // 6. Sort bids on the client
                        bids.sort((a, b) => {
                            const nameComparison = a.playerName.localeCompare(b.playerName);
                            if (nameComparison !== 0) return nameComparison;

                            const amountComparison = b.amount - a.amount;
                            if (amountComparison !== 0) return amountComparison;

                            const dateA = a.timestamp?.toDate() || new Date(0);
                            const dateB = b.timestamp?.toDate() || new Date(0);
                            return dateA - dateB;
                        });

                        round.bids = bids;
                        return round;
                    }));

                    setClosedRounds(roundsData);
                } catch (err) {
                    console.error("Error fetching closed rounds:", err);
                    notify.error("Kon gesloten blinde pool rondes niet ophalen.");
                } finally {
                    setLoading(false);
                }
            };
            fetchClosedRounds();
        }
    }, [open, auctionId, notify]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Overzicht Blinde Pool Biedingen</DialogTitle>
            <DialogContent>
                {loading ? (
                    <CircularProgress />
                ) : closedRounds.length === 0 ? (
                    <Typography>Er zijn nog geen afgesloten rondes.</Typography>
                ) : (
                    closedRounds.map(round => (
                        <Box key={round.id} sx={{ mb: 4 }}>
                            <Typography variant="h6">
                                Ronde {round.roundNumber}
                            </Typography>
                            <Typography variant="caption" display="block" gutterBottom>
                                Afgesloten op {new Date(round.closedAt.seconds * 1000).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            <Box sx={{
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                overflow: 'hidden',
                            }}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Speler</TableCell>
                                                <TableCell>Club</TableCell>
                                                <TableCell>Positie</TableCell>
                                                <TableCell>Deelnemer</TableCell>
                                                <TableCell align="right">Bedrag</TableCell>
                                                <TableCell>Datum/tijd</TableCell>
                                                <TableCell align="center">Toegekend</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {round.bids.map(bid => (
                                                <TableRow key={bid.id}>
                                                    <TableCell>{bid.playerName}</TableCell>
                                                    <TableCell>{bid.playerClub}</TableCell>
                                                    <TableCell>{POSITION_MAP[bid.playerPosition] || bid.playerPosition}</TableCell>
                                                    <TableCell>{bid.participantName}</TableCell>
                                                    <TableCell align="right">{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(bid.amount)}</TableCell>
                                                    <TableCell>{bid.timestamp?.toDate().toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</TableCell>
                                                    <TableCell align="center">
                                                        {bid.isWinner && <CheckCircleIcon color="success" />}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    ))
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Sluiten</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BidsOverviewModal;
